import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { PageHeader } from "@/components/page-header"
import { Tabs, TabsContent } from "@/components/ui/tabs"
import type { PendingArrival } from "@/features/intake/api/types"
import { IntakeKpiStrip } from "@/features/intake/components/intake-kpi-strip"
import { IntakeRowSkeletonList } from "@/features/intake/components/intake-row-skeleton"
import {
  IntakeTabBar,
  type IntakeTab,
} from "@/features/intake/components/intake-tab-bar"
import { IntakeTable } from "@/features/intake/components/intake-table"
import { WaveInDialog } from "@/features/intake/components/wave-in-dialog"
import {
  useLoads,
  usePendingArrivals,
} from "@/features/intake/hooks/use-intake"

export function IntakePage() {
  const { t } = useTranslation("intake")

  const [tab, setTab] = useState<IntakeTab>("ALL")
  const [waveInTarget, setWaveInTarget] = useState<PendingArrival | null>(null)

  const { data: pending, isLoading: loadingPending } = usePendingArrivals()
  const { data: loads, isLoading: loadingLoads } = useLoads()

  const counts = useMemo(() => {
    const pendingCount = pending?.length ?? 0
    const list = loads ?? []
    const scale = list.filter((l) => l.status === "SCALE").length
    const quality = list.filter((l) => l.status === "QUALITY").length
    const completed = list.filter((l) => l.status === "COMPLETED").length
    return {
      ALL: pendingCount + scale + quality + completed,
      PENDING: pendingCount,
      SCALE: scale,
      QUALITY: quality,
      COMPLETED: completed,
    } satisfies Record<IntakeTab, number>
  }, [pending, loads])

  const isLoading = loadingPending || loadingLoads
  const onYard = counts.ALL - counts.COMPLETED

  const handleSelectPending = (p: PendingArrival) => setWaveInTarget(p)

  const renderPending = () => {
    const items = pending ?? []
    if (items.length === 0) {
      return (
        <EmptyState
          title={t("empty.pendingTitle")}
          description={t("empty.pendingDescription")}
        />
      )
    }
    return (
      <IntakeTable
        rows={items.map((p) => ({ kind: "pending", pending: p }))}
        onSelectPending={handleSelectPending}
      />
    )
  }

  const renderLoads = (
    filterStatus: "SCALE" | "QUALITY" | "COMPLETED" | null,
  ) => {
    const items = (loads ?? []).filter(
      (l) => filterStatus === null || l.status === filterStatus,
    )
    if (items.length === 0) {
      const titleKey =
        filterStatus === "SCALE"
          ? "empty.scaleTitle"
          : filterStatus === "QUALITY"
            ? "empty.qualityTitle"
            : filterStatus === "COMPLETED"
              ? "empty.completedTitle"
              : "empty.allTitle"
      const descKey =
        filterStatus === "SCALE"
          ? "empty.scaleDescription"
          : filterStatus === "QUALITY"
            ? "empty.qualityDescription"
            : filterStatus === "COMPLETED"
              ? "empty.completedDescription"
              : "empty.allDescription"
      return <EmptyState title={t(titleKey)} description={t(descKey)} />
    }
    return (
      <IntakeTable
        rows={items.map((l) => ({ kind: "load", load: l }))}
        onSelectPending={handleSelectPending}
      />
    )
  }

  const renderAll = () => {
    const pendingItems = pending ?? []
    const loadItems = loads ?? []
    if (pendingItems.length + loadItems.length === 0) {
      return (
        <EmptyState
          title={t("empty.allTitle")}
          description={t("empty.allDescription")}
        />
      )
    }
    const rows = [
      ...pendingItems.map(
        (p) => ({ kind: "pending", pending: p }) as const,
      ),
      ...loadItems.map((l) => ({ kind: "load", load: l }) as const),
    ]
    return <IntakeTable rows={rows} onSelectPending={handleSelectPending} />
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow={t("eyebrow")}
        title={t("title")}
        description={t("subtitle", {
          onYard,
          processedToday: counts.COMPLETED,
        })}
      />

      <IntakeKpiStrip pending={pending ?? []} />

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-lg font-semibold tracking-tight">
            {t("flow.title")}
          </h2>
          <span className="text-muted-foreground text-sm tabular-nums">
            {t("flow.onYard", { count: onYard })}
          </span>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as IntakeTab)}>
          <IntakeTabBar counts={counts} />
          <div className="pt-4">
            <TabsContent value="ALL">
              {isLoading ? <IntakeRowSkeletonList /> : renderAll()}
            </TabsContent>
            <TabsContent value="PENDING">
              {isLoading ? <IntakeRowSkeletonList /> : renderPending()}
            </TabsContent>
            <TabsContent value="SCALE">
              {isLoading ? <IntakeRowSkeletonList /> : renderLoads("SCALE")}
            </TabsContent>
            <TabsContent value="QUALITY">
              {isLoading ? <IntakeRowSkeletonList /> : renderLoads("QUALITY")}
            </TabsContent>
            <TabsContent value="COMPLETED">
              {isLoading ? <IntakeRowSkeletonList /> : renderLoads("COMPLETED")}
            </TabsContent>
          </div>
        </Tabs>
      </div>

      <WaveInDialog
        pending={waveInTarget}
        open={waveInTarget !== null}
        onOpenChange={(open) => {
          if (!open) setWaveInTarget(null)
        }}
      />
    </div>
  )
}
